<template>
  <div class="search-page px-4 pt-7.5 xl:container xl:mx-auto relative">
    <form ref="searchForm" name="searchForm" method="get" class="flex flex-col gap-16 form-search-page">
      <div class="flex flex-col gap-4">
        <h2
          class="text-3xl-2alt leading-tighter md:text-4xl-2alt font-semibold tracking-3tight md:tracking-normal"
        >
          {{ data.design.title }}
        </h2>
        <h3 class="font-medium text-lg leading-5tighter text-geb-gray-500">{{ data.pager.total }} {{ 'resultados encontrados'|replaceLabel(data.design.labels) }}</h3>
        <div v-html="data.design.content" v-if="data.design.content"></div>
      </div>

      <div class="flex flex-wrap md:flex-nowrap md:gap-7.5">
        <div class="search-page__sidebar w-full md:w-1/4 lg:w-78 lg:flex-shrink-0 mb-5">
          <h3 class="text-xl leading-2tighter font-semibold mb-3">{{ 'Refinar'|replaceLabel(data.design.labels) }}</h3>
          <input
            :placeholder="'Término de búsqueda'|replaceLabel(data.design.labels)"
            type="text"
            name="filters[keyword]"
            class="w-full h-14 sm:h-10.5 px-3 py-2 outline-none border border-geb-gray-50 focus:border-geb-dark-900 focus:border-opacity-50 rounded-1.5sm text-2sm leading-4tighter tracking-1.8tight"
            v-on:keydown.enter.prevent="submitSearch()"
            :value="
              data.applied_filters.keyword ? data.applied_filters.keyword : ''
            "
          />

          <div
            v-if="
              data.design.sorting_options && data.design.sorting_options.length
            "
            class="sorting-container mt-5"
          >
            <h4 class="text-2lg leading-4tighter mb-1.5">{{ 'Ordenar por'|replaceLabel(data.design.labels) }}</h4>

            <div
              v-for="(item, index) in data.design.sorting_options"
              :key="`sorting-opt-${index}`"
              class="search-sidebar__option mb-2"
            >
              <label v-on:click="submitSearch()">
                <input name="sort" :value="item.value" type="radio" v-bind:checked="data.design.sorted_by == item.value" />
                {{ item.name|replaceLabel(data.design.labels) }}
              </label>
            </div>
          </div>

          <div
            v-for="(aggrItem, aggIndex) in dataAggregations"
            :key="`aggr-container-${aggIndex}`"
            class="filter-container mt-3.25"
          >
            <h4 class="text-2lg leading-4tighter mb-1.5">{{ aggrItem.name|replaceLabel(data.design.labels) }}</h4>

            <div
              v-for="(item, index) in aggrItem.entries"
              :key="`filter-opt-${aggIndex}-${index}`"
              class="search-sidebar__option mb-2"
            >
              <label :for="`filter-opt-${aggIndex}-${item.value}`">
                <input
                  :id="`filter-opt-${aggIndex}-${item.value}`"
                  :name="`filters[${aggIndex}][]`"
                  :value="item.value"
                  :type="aggrItem.multiple ? 'checkbox' : 'radio'"
                  v-on:change="submitSearch()"
                  v-bind:checked="data.applied_filters[aggIndex] && data.applied_filters[aggIndex].indexOf(item.value) >= 0"
                />
                {{ item.name|replaceLabel(data.design.labels) }} ({{ item.count }})
              </label>
            </div>
          </div>
        </div>
        <div
          v-if="data.items"
          class="search-page__results md:w-3/4 lg:w-full mb-5"
        >
          <div class="results-container">
            <a
              v-for="(item, index) in data.items"
              :key="index"
              :href="item.link"
              class="search-results__item mb-9"
            >
              <span class="content-type uppercase">{{ item.type }}</span>
              <h3 class="content-link">{{ item.title }}</h3>

              <p class="content-resume" v-html="item.resume.trim()"></p>
            </a>
          </div>

          <div class="pagination-container text-geb-green-secondary" v-if="data.pager.last_page > 1">
            <a
              :href="getUpdatedPageLink(data.pager.previous_page)"
              class="page-link prev hover:text-geb-green-900"
              v-bind:disabled="data.pager.previous_page >= data.pager.current_page"
            >
              <i class="link-icon" v-icon:arrow-right-link-alt></i> {{ 'Anterior'|replaceLabel(data.design.labels) }}
            </a>

            <span class="page-number">{{
              data.pager.current_page
            }}</span>

            <a
              :href="getUpdatedPageLink(data.pager.next_page)"
              class="page-link next hover:text-geb-green-900"
              v-bind:disabled="!data.pager.has_next"
            >
              {{ 'Siguiente'|replaceLabel(data.design.labels) }} <i class="link-icon" v-icon:arrow-right-link-alt></i>
            </a>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
export default {
  name: "SearchPage",
  props: {
    data: {
      type: Object,
      default: () => {},
      required: true,
    },
    apiUrl: {
      type: String,
      required: true,
      default: "",
    }
  },
  data() {
    return {
      isLoading: false,
    };
  },
  filters: {
    replaceLabel: function(value, labelsMap) {
      // console.log(value, labelsMap);
      return labelsMap[value.toLowerCase()] ?? value;
    }
  },
  computed: {
    dataAggregations() {
      const asArray = Object.entries(this.data.aggregations);
      const filtered = asArray.filter(([key, value]) => !!value);
      return Object.fromEntries(filtered);
    }
  },
  methods: {
    async submitSearch() {
      if (this.isLoading) return;
      const actualUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;

      const formData = new FormData(document.forms["searchForm"]);
      const data = [...formData.entries()];
      const formString = data.map((x) => `${x[0]}=${x[1]}`).join("&");

      const response = await fetch(`${this.apiUrl}?${formString}`);
      this.data = await response.json();

      window.history.pushState(
        { path: `${actualUrl}?${formString}` },
        "",
        `${actualUrl}?${formString}`
      );

      this.isLoading = false;
    },
    getUpdatedPageLink(page) {
      const searchParams = window.location.search;

      var regex = new RegExp("([?;&])page[^&;]*[;&]?");
      var query = searchParams.replace(regex, "$1").replace(/&$/, "");

      return (
        (query.length > 2 ? query + "&" : "?") +
        (page ? "page" + "=" + page : "")
      );
    },
  },
};
</script>